import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Upload, Send, Image as ImageIcon, FileText, Check, Crown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import DriverRulesModal from "@/components/chauffeur/DriverRulesModal";
import { errorMessage } from "@/lib/errors";
import PasswordStrengthChecklist, { passwordIsStrong } from "@/components/auth/PasswordStrengthChecklist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_OPTIONS = [
  { value: "taxi", label: "Taxi" },
  { value: "delivery", label: "Delivery" },
];

export default function DriverSignupPage() {
  const nav = useNavigate();
  const licRef = useRef(null);
  const vehRef = useRef(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    license_plate: "", vehicle_make: "", vehicle_model: "", vehicle_color: "", vehicle_year: "",
    license_photo_url: "", vehicle_photo_url: "",
    service_modes: ["taxi", "delivery"],
  });
  const [uploading, setUploading] = useState({ license: false, vehicle: false });

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleMode = (m) => {
    setForm((s) => {
      const cur = new Set(s.service_modes);
      cur.has(m) ? cur.delete(m) : cur.add(m);
      const next = Array.from(cur);
      return { ...s, service_modes: next.length ? next : [m] };
    });
  };

  const uploadDoc = async (kind, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      // Use the public driver-doc endpoint; works without auth since signup is pre-account.
      // For pre-account flow we'll upload to a temp anon path via a prepared backend call.
      // Simplest: call /chauffeur/drivers/upload-doc as the just-created user *after*
      // user creation. To support pre-account upload we use the public Emergent file
      // hosting via FormData and trust the backend to clean up orphans.
      const fd = new FormData();
      fd.append("file", file);
      // Pre-account flow: we hold the file in browser memory until signup, then attach
      // its data URL. That keeps the public surface small for unauthenticated callers.
      const reader = new FileReader();
      const dataUrl = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      upd(kind === "license" ? "license_photo_url" : "vehicle_photo_url", dataUrl);
      toast.success(`${kind === "license" ? "License" : "Vehicle"} photo attached`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const submit = async () => {
    if (!rulesAccepted) { setShowRules(true); return; }
    if (!form.name || !form.email || !form.password) { toast.error("Fill personal details"); return; }
    if (!passwordIsStrong(form.password)) {
      toast.error("Password must satisfy all the rules below the password field");
      return;
    }
    if (!form.license_plate || !form.vehicle_make || !form.vehicle_model) { toast.error("Fill vehicle details"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
        accepted_rules: true,
      };
      const res = await axios.post(`${API}/chauffeur/drivers/signup`, payload);
      if (res.data?.created || res.data?.reused) {
        toast.success("Application submitted — admin will review shortly");
        // Auto-login + redirect to driver dashboard
        try {
          const lr = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password });
          localStorage.setItem("auth_token", lr.data.access_token);
        } catch {/* */}
        nav("/driver");
      } else {
        toast.error("Unexpected response");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Signup failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="driver-signup-page" className="pt-24 min-h-screen bg-luxury-black">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1 inline-flex items-center gap-1.5"><Crown size={12} /> Driver-First Signup</p>
          <h1 className="font-heading text-3xl text-white mb-2">Drive with Aristocrats</h1>
          <p className="text-white/50 font-body text-sm">Apply once. Drive taxi, deliver food, or both. Earn after a flat 7% platform fee.</p>
        </div>

        {/* STEP indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`flex-1 h-1 ${n <= step ? "bg-gold" : "bg-white/10"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-xs font-body uppercase tracking-wider text-white/60">Step 1 — Personal</p>
            <Input data-testid="signup-name" value={form.name} onChange={(e) => upd("name", e.target.value)}
              placeholder="Full name" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)}
              placeholder="Email" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="signup-password" type="password" value={form.password} onChange={(e) => upd("password", e.target.value)}
              placeholder="Password (12+ chars, mixed case, number, special)" className="bg-white/5 border-white/15 text-white" />
            <PasswordStrengthChecklist password={form.password} data-testid="signup-password-rules" />
            <Input data-testid="signup-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)}
              placeholder="Phone (optional)" className="bg-white/5 border-white/15 text-white" />
            <button data-testid="next-step-1" onClick={() => setStep(2)}
              disabled={!form.name || !form.email || !passwordIsStrong(form.password)}
              className="w-full bg-gold text-luxury-black py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-40">
              Next — Vehicle
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-xs font-body uppercase tracking-wider text-white/60">Step 2 — Vehicle &amp; Modes</p>
            <Input data-testid="signup-plate" value={form.license_plate} onChange={(e) => upd("license_plate", e.target.value)}
              placeholder="License plate" className="bg-white/5 border-white/15 text-white" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="signup-make" value={form.vehicle_make} onChange={(e) => upd("vehicle_make", e.target.value)}
                placeholder="Make" className="bg-white/5 border-white/15 text-white" />
              <Input data-testid="signup-model" value={form.vehicle_model} onChange={(e) => upd("vehicle_model", e.target.value)}
                placeholder="Model" className="bg-white/5 border-white/15 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="signup-color" value={form.vehicle_color} onChange={(e) => upd("vehicle_color", e.target.value)}
                placeholder="Color" className="bg-white/5 border-white/15 text-white" />
              <Input data-testid="signup-year" type="number" value={form.vehicle_year} onChange={(e) => upd("vehicle_year", e.target.value)}
                placeholder="Year" className="bg-white/5 border-white/15 text-white" />
            </div>
            <div>
              <p className="text-white/60 text-xs font-body uppercase tracking-wider mt-2 mb-2">I want to serve as</p>
              <div className="flex gap-2">
                {MODE_OPTIONS.map((m) => {
                  const on = form.service_modes.includes(m.value);
                  return (
                    <button key={m.value} data-testid={`mode-${m.value}`} onClick={() => toggleMode(m.value)}
                      className={`flex-1 py-3 text-xs font-body uppercase tracking-wider border transition-colors ${on ? "bg-gold/15 text-gold border-gold/40" : "border-white/15 text-white/50 hover:text-white"}`}>
                      <Check size={12} className={`inline mr-1 ${on ? "" : "opacity-0"}`} /> {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setStep(1)} className="px-4 py-3 border border-white/15 text-white/60 text-xs font-body uppercase tracking-wider hover:text-white">Back</button>
              <button data-testid="next-step-2" onClick={() => setStep(3)}
                disabled={!form.license_plate || !form.vehicle_make || !form.vehicle_model}
                className="flex-1 bg-gold text-luxury-black py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-40">
                Next — Documents
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-xs font-body uppercase tracking-wider text-white/60">Step 3 — Documents &amp; Rules</p>
            <DocBox kind="license" title="Driver License" Icon={FileText} value={form.license_photo_url}
              uploading={uploading.license} fileRef={licRef} onPick={(f) => uploadDoc("license", f)} />
            <DocBox kind="vehicle" title="Vehicle Photo" Icon={ImageIcon} value={form.vehicle_photo_url}
              uploading={uploading.vehicle} fileRef={vehRef} onPick={(f) => uploadDoc("vehicle", f)} />
            <button data-testid="open-rules" onClick={() => setShowRules(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-white/15 text-white/70 text-xs font-body uppercase tracking-wider hover:text-white">
              <FileText size={14} /> {rulesAccepted ? "Rules Accepted ✓" : "Read & Accept Driver Rules"}
            </button>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setStep(2)} className="px-4 py-3 border border-white/15 text-white/60 text-xs font-body uppercase tracking-wider hover:text-white">Back</button>
              <button data-testid="signup-submit" onClick={submit} disabled={submitting || !rulesAccepted}
                className="flex-1 flex items-center justify-center gap-2 bg-gold text-luxury-black py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-40">
                <Send size={14} /> {submitting ? "Submitting…" : "Apply to Drive"}
              </button>
            </div>
          </div>
        )}

        <DriverRulesModal open={showRules} onClose={() => setShowRules(false)} defaultAccepted={rulesAccepted}
          onAccept={() => { setRulesAccepted(true); setShowRules(false); toast.success("Rules accepted"); }} />
      </div>
    </div>
  );
}

function DocBox({ kind, title, Icon, value, uploading, fileRef, onPick }) {
  return (
    <div className={`relative border-2 border-dashed rounded-md p-4 transition-colors ${value ? "border-gold/40 bg-gold/5" : "border-white/15 bg-white/[0.02]"}`}>
      <input ref={fileRef} type="file" accept="image/*,application/pdf"
        data-testid={`upload-${kind}-input`}
        onChange={(e) => onPick(e.target.files?.[0])} className="hidden" />
      <div className="flex items-center gap-3">
        <Icon className={value ? "text-gold" : "text-white/40"} />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-body">{title}</p>
          <p className="text-white/40 text-xs font-body">{value ? "Attached" : "JPG / PNG / PDF · max 10 MB"}</p>
        </div>
        <button data-testid={`upload-${kind}-btn`} onClick={() => fileRef.current?.click()}
          className="bg-gold/15 text-gold px-3 py-1.5 text-xs font-body uppercase tracking-wider border border-gold/30 hover:bg-gold/25 transition-colors disabled:opacity-50"
          disabled={uploading}>
          <Upload size={12} className="inline mr-1" /> {uploading ? "…" : (value ? "Replace" : "Upload")}
        </button>
      </div>
      {value && value.startsWith("data:image") && (
        <img src={value} alt={title} className="mt-3 w-full max-h-44 object-contain rounded-sm" data-testid={`preview-${kind}`} />
      )}
    </div>
  );
}
